import { useEffect } from "react";
import { SITE } from "@/data/site";

/**
 * SEO — imperatively updates <title>, <meta>, <link rel=canonical>, and JSON-LD.
 * Uses direct DOM manipulation to avoid react-helmet-async quirks with pre-existing tags.
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export default function SEO({ title, description, keywords, path, image, type = "website", jsonLd }) {
  useEffect(() => {
    const url = `${SITE.origin}${path || "/"}`;
    const fullTitle = title ? `${title} | ${SITE.shortName}` : `${SITE.name} | Ankara Bağlıca Güzellik Merkezi`;
    const desc = description || "Ankara'nın En Güvenilir Güzellik ve Ayak Sağlığı Merkezi: Kaliteli hizmet, sağlığınızı ve güzelliğinizi ön planda tutuyoruz.";
    const img = image
      ? (image.startsWith("http") ? image : `${SITE.origin}/${image.replace(/^\//, "")}`)
      : `${SITE.origin}/og-image.jpg`;

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', { name: "description", content: desc });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords || "MediEste, bağlıca güzellik, lazer epilasyon, medikal ayak bakımı, kalıcı makyaj, podoloji" });

    upsertLink("canonical", url);

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE.name });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: img });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });

    if (jsonLd) upsertJsonLd("page-jsonld", jsonLd);
    else removeJsonLd("page-jsonld");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, path, image, type, JSON.stringify(jsonLd)]);

  return null;
}
